/**
 * Presale (odbm.publication_date) + restock (odbm.expected_restock_date) PDP handler.
 *
 * A product whose odbm.publication_date is in the future is a "presale": it must
 * be ordered on its own (exactly one presale product, any quantity, nothing else),
 * mirroring the shipped `preorder-isolation-blocker` Function (the checkout
 * backstop). A variant with odbm.expected_restock_date set (and out of stock) is
 * a display-only "Expected restock {date}" case.
 *
 * Scoped per section. Each presale-capable section injects its own data script
 * carrying its section id (`data-odbm-presale-data="<section.id>"`), and every DOM
 * lookup runs inside that section's <product-info data-section="<section.id>"> root.
 * One handler per instance, so an ODBM Featured Product and the PDP hero can both
 * render presale on one page without fighting over the same elements.
 *
 * Data comes from the injecting section (odbm-main-product.liquid,
 * odbm-featured-product.liquid), a per-variant map
 * (id -> { type: "presale" | "restock", text: "<localized line>" }). Liquid owns
 * the future-date gate, the out-of-stock check, and the localized/formatted line
 * text (client variant JSON carries no metafields). This module:
 *  - renders that line into [data-odbm-date-line] in the buy-buttons block, and
 *    updates it on variant change (the line lives here, not in the vendor
 *    Inventory block, because the ODBM product templates dropped that block);
 *  - for a presale variant, hides the Add-to-cart SUBMIT button (+ the dynamic
 *    checkout) and shows the "Pre-order now" button, which the theme renders in
 *    the submit's place. It hides only the submit, not the whole form, so an
 *    inline quantity pill stays visible;
 *  - on Pre-order click, with an EMPTY cart adds the variant + quantity and goes
 *    to an isolated checkout; with a NON-empty cart hides the quantity + button
 *    and explains in the alert; if the add fails (e.g. not purchasable), shows
 *    an "unavailable" message instead of silently doing nothing.
 * The Pre-order button is a plain button (cart add + redirect), not
 * `payment_button`.
 */

const CART_URL = "/cart.js";
const DEFAULT_CART_ADD_URL = "/cart/add.js";
const DEFAULT_CHECKOUT_URL = "/checkout";
/** Injected per section; its value is the section id used to find the root. */
const DATA_SELECTOR = "[data-odbm-presale-data]";

const getCartAddUrl = () =>
  window.FoxThemeSettings?.routes?.cart_add_url || DEFAULT_CART_ADD_URL;

const VARIANT_INPUT_SELECTOR =
  ".main-product-form input[name='id'], .product-form input[name='id']";

const getVariantFromForm = (root) => {
  const input = root.querySelector(VARIANT_INPUT_SELECTOR);
  return input?.value ? input.value : null;
};

const getVariantFromUrl = (url) => {
  try {
    return new URLSearchParams(new URL(url).search).get("variant");
  } catch (error) {
    console.error("Error parsing URL:", error);
    return null;
  }
};

/**
 * The section's selected variant. Falls back to the URL's ?variant= for the
 * initial paint before a picker has populated the input. A scoped section that
 * isn't the page's main product simply misses on that fallback — the id won't be
 * in its own variantLines map — which degrades to "no presale line".
 */
const getCurrentVariant = (root) =>
  getVariantFromForm(root) || getVariantFromUrl(window.location.href);

/** Show or hide an element via inline display, no-op if the element is missing. */
const toggleDisplay = (element, show) => {
  if (element) {
    element.style.display = show ? "" : "none";
  }
};

/**
 * Handles the presale/restock date line, the Add-to-cart <-> Pre-order swap, and
 * the isolated-cart rule.
 */
class PresaleHandler {
  /**
   * @param {ParentNode} [root] element to scope every lookup to; `document` when
   *   a page has a single presale section (and in tests).
   * @param {Element|null} [dataEl] the injected data script. Defaults to the
   *   first one inside `root`.
   */
  constructor(root = document, dataEl = null) {
    this.root = root;
    this.dataEl = dataEl ?? root.querySelector(DATA_SELECTOR);
    this.variantLines = this.getVariantLines();
    this.init();
  }

  /**
   * Parse the injected per-variant map. Returns {} on any problem so a malformed
   * blob degrades to "no presale/restock variants" (normal Add to cart).
   * @returns {Object<string, {type: string, text: string}>}
   */
  getVariantLines() {
    try {
      if (!this.dataEl) {
        return {};
      }
      const parsed = JSON.parse(this.dataEl.textContent);
      const lines = parsed?.variant_lines;
      return lines && typeof lines === "object" ? lines : {};
    } catch (error) {
      console.error("Error parsing presale data:", error);
      return {};
    }
  }

  /**
   * @param {string|null} variantId
   * @returns {boolean}
   */
  isPresaleVariant(variantId) {
    return this.variantLines[String(variantId)]?.type === "presale";
  }

  get addToCartForm() {
    return this.root.querySelector(".f-main-product-form, .product-form");
  }

  /**
   * The Add-to-cart submit button plus the dynamic-checkout wrapper. Presale
   * toggles only these, NOT the whole form, so a quantity pill rendered inline
   * with the button stays visible. Missing nodes are filtered out.
   * @returns {Element[]}
   */
  get addToCartControls() {
    const form = this.addToCartForm;
    if (!form) {
      return [];
    }
    return [
      form.querySelector("[name='add']"),
      form.querySelector(".f-product-form__button-dynamic"),
    ].filter(Boolean);
  }

  /** The cart-isolation alert; also carries the localized copy. */
  get preorderAlert() {
    return this.root.querySelector("[data-odbm-preorder]");
  }

  get preorderButton() {
    return this.root.querySelector("[data-odbm-preorder-btn]");
  }

  get preorderMessage() {
    return this.root.querySelector("[data-odbm-preorder-message]");
  }

  /** The quantity pill's wrapper, inline or standalone (both id QuantityForm-…). */
  get quantityWrapper() {
    const input = this.root.querySelector("input[name='quantity']");
    return input?.closest("[id^='QuantityForm-']") ?? null;
  }

  /**
   * What a blocked cart hides. Not the row that holds them — the date line
   * lives there too, and it should outlive the controls.
   * @returns {Element[]}
   */
  get preorderControls() {
    return [this.preorderButton, this.quantityWrapper].filter(Boolean);
  }

  get dateLineEl() {
    return this.root.querySelector("[data-odbm-date-line]");
  }

  /**
   * Quantity from the shared product quantity input (min 1).
   * @returns {number}
   */
  getQuantity() {
    const input = this.root.querySelector("input[name='quantity']");
    const value = Number.parseInt(input?.value ?? "1", 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * Render the presale/restock line for the given variant, or hide it.
   * @param {string|null} variantId
   */
  updateDateLine(variantId) {
    const el = this.dateLineEl;
    if (!el) {
      return;
    }
    const entry = this.variantLines[String(variantId)];
    if (entry?.text) {
      el.textContent = entry.text;
      el.dataset.type = entry.type || "";
      el.hidden = false;
    } else {
      el.textContent = "";
      el.hidden = true;
    }
  }

  /** Fill the alert, or hide it when text is empty. */
  showMessage(text) {
    const message = this.preorderMessage;
    if (message) {
      message.textContent = text || "";
    }
    const alert = this.preorderAlert;
    if (alert) {
      alert.hidden = !text;
    }
  }

  /** Show or hide the quantity pill + Pre-order button as one unit. */
  setPreorderControlsVisible(visible) {
    for (const control of this.preorderControls) {
      toggleDisplay(control, visible);
    }
  }

  /**
   * A non-empty cart can't hold a presale, so there is nothing to configure:
   * hide the quantity + Pre-order button outright and explain in the alert.
   * @param {boolean} blocked
   */
  setBlockedState(blocked) {
    this.setPreorderControlsVisible(!blocked);
    this.showMessage(
      blocked ? (this.preorderAlert?.dataset.messageCartNotEmpty ?? "") : ""
    );
  }

  /**
   * Fetch the current cart line-item count. Returns 0 if the request fails, so a
   * transient error never wrongly blocks a legitimate presale purchase (the
   * shipped Function still blocks a mixed cart at checkout).
   * @returns {Promise<number>}
   */
  async getCartCount() {
    try {
      const response = await fetch(CART_URL, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        return 0;
      }
      const cart = await response.json();
      return Number(cart?.item_count) || 0;
    } catch (error) {
      console.error("Error reading cart:", error);
      return 0;
    }
  }

  /**
   * Refresh the blocked state from live cart contents. Bails if the buyer moved
   * off the presale variant while the request was in flight — the response
   * would otherwise hide the quantity pill on a normal variant.
   */
  async refreshCartState() {
    const count = await this.getCartCount();
    if (!this.isPresaleVariant(getCurrentVariant(this.root))) {
      return;
    }
    this.setBlockedState(count > 0);
  }

  /**
   * Navigate the browser. Isolated so tests can assert the destination without
   * triggering a real navigation.
   */
  redirect(url) {
    window.location.href = url;
  }

  /**
   * Add the given variant + quantity to the cart.
   * @returns {Promise<boolean>} true on success
   */
  async addToCart(variantId, quantity) {
    try {
      const response = await fetch(getCartAddUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });
      return response.ok;
    } catch (error) {
      console.error("Error adding presale item to cart:", error);
      return false;
    }
  }

  /**
   * Pre-order click: re-check the cart, and if empty add this variant + quantity
   * and go to checkout. Non-empty cart blocks with a message; a failed add shows
   * the "unavailable" message rather than silently doing nothing.
   * @returns {Promise<void>}
   */
  async handlePreorderClick() {
    const variantId = getCurrentVariant(this.root);
    if (!variantId) {
      return;
    }

    const count = await this.getCartCount();
    if (count > 0) {
      this.setBlockedState(true);
      return;
    }

    const added = await this.addToCart(variantId, this.getQuantity());
    if (added) {
      this.redirect(DEFAULT_CHECKOUT_URL);
    } else {
      this.showMessage(this.preorderAlert?.dataset.messageUnavailable ?? "");
    }
  }

  /**
   * Update the date line and toggle Add-to-cart vs Pre-order for the current
   * variant. The cart-state check runs asynchronously and is not awaited here.
   */
  updatePresaleUI() {
    const variantId = getCurrentVariant(this.root);
    this.updateDateLine(variantId);

    const isPresale = this.isPresaleVariant(variantId);
    for (const control of this.addToCartControls) {
      toggleDisplay(control, !isPresale);
    }
    // Undo any blocked state from the previous variant before re-deciding.
    this.setPreorderControlsVisible(true);
    this.showMessage("");
    toggleDisplay(this.preorderButton, isPresale);

    if (isPresale) {
      this.refreshCartState();
    }
  }

  /**
   * Watch the variant input for value changes (covers pickers that mutate it
   * without emitting the pub/sub event).
   */
  setupVariantObserver() {
    const input = this.root.querySelector(VARIANT_INPUT_SELECTOR);
    if (!input) {
      return;
    }
    let lastValue = input.value;
    const rerun = () => {
      if (input.value !== lastValue) {
        lastValue = input.value;
        this.updatePresaleUI();
      }
    };
    const observer = new MutationObserver(rerun);
    observer.observe(input, { attributes: true, attributeFilter: ["value"] });
    input.addEventListener("change", rerun);
  }

  /** Subscribe to the Zest variant-change pub/sub so the UI tracks the picker. */
  setupPubSubListener() {
    if (window.FoxThemeEvents?.subscribe) {
      window.FoxThemeEvents.subscribe("variant:change", () => {
        this.updatePresaleUI();
      });
    }
    if (window.subscribe && window.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
        this.updatePresaleUI();
      });
    }
  }

  /** Wire the Pre-order button click. */
  setupPreorderButton() {
    const button = this.preorderButton;
    if (button) {
      button.addEventListener("click", () => {
        this.handlePreorderClick();
      });
    }
  }

  init() {
    this.setupVariantObserver();
    this.setupPubSubListener();
    this.setupPreorderButton();
    this.updatePresaleUI();
  }
}

/**
 * One handler per injected data script, scoped to that section's <product-info>.
 * A single script with no matching root falls back to document scope (the shape
 * before sections were scoped); with several, an unmatched one is skipped rather
 * than left to drive another section's date line.
 * @returns {PresaleHandler[]}
 */
const initPresaleHandlers = () => {
  const dataEls = [...document.querySelectorAll(DATA_SELECTOR)];
  return dataEls
    .map((dataEl) => {
      const sectionId = dataEl.dataset.odbmPresaleData;
      const root = sectionId
        ? document.querySelector(`product-info[data-section="${sectionId}"]`)
        : null;
      if (root) {
        return new PresaleHandler(root, dataEl);
      }
      if (dataEls.length === 1) {
        return new PresaleHandler(document, dataEl);
      }
      console.error(
        `No <product-info data-section="${sectionId}"> for presale data; skipping to avoid driving another section.`
      );
      return null;
    })
    .filter(Boolean);
};

document.addEventListener("DOMContentLoaded", () => {
  initPresaleHandlers();
});
